import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useUpdateAsset, type Asset } from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';

interface RenameAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  asset: Asset;
}

/** Rename a library asset (PATCH /assets/{id}). Display name only — the storage
 *  object key is immutable. */
export function RenameAssetDialog({ open, onOpenChange, serviceUuid, asset }: RenameAssetDialogProps) {
  const { show } = useToast();
  const [name, setName] = useState(asset.name);

  const update = useUpdateAsset(serviceUuid, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Asset renamed' });
      trackFeatureAction('fastchannel', 'asset', 'edit');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not rename asset' }),
  });

  const valid = name.trim().length > 0 && name.trim() !== asset.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename asset</DialogTitle>
          <DialogDescription>The display name in the content library.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Name" required>
            {(props) => (
              <Input
                {...props}
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valid && !update.isPending) {
                    update.mutate({ assetId: asset.id, name: name.trim() });
                  }
                }}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || update.isPending}
            onClick={() => update.mutate({ assetId: asset.id, name: name.trim() })}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
